import { useTranslation } from "react-i18next";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Tag } from "components/designV2/tag/tag";
import { ListRow } from "components/designV2/listRow/listRow";
import { ColorV2 } from "config/colors";
import { ApplicationStatus } from "model/Application";
import type { CompanyGigApplications } from "model/Gig";
import { toApplicationStatusChip } from "web-apps/company/components/applications/applicationList.display.logic";
import type { TeamPublicApplicationAction } from "./teamPublicDetailsModal.contract";
import {
  ActionButtons,
  ApplicationStatusTagWrapper,
  RequestedRateBlock,
  RequestedRateLabel,
  RequestedRateValue,
} from "./teamPublicDetailsModal.styled";

const ASSIGN_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
};

const DECLINE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.NegativeRedLight,
  activeBackgroundColor: ColorV2.WithdrawnPink,
};

interface TeamPublicDetailsModalApplicationRowBaseProps {
  readonly application: CompanyGigApplications;
  readonly avatarUrl?: string;
  readonly isPublicShift: boolean;
  readonly isBusy: boolean;
  readonly onOpenMotivationalLetterModal: (motivationalLetter: string) => void;
}

interface TeamPublicDetailsModalApplicationRowManageableProps extends TeamPublicDetailsModalApplicationRowBaseProps {
  readonly canManageApplication: true;
  readonly onOpenApplicationActionConfirm: (
    action: TeamPublicApplicationAction,
  ) => void;
}

interface TeamPublicDetailsModalApplicationRowReadOnlyProps extends TeamPublicDetailsModalApplicationRowBaseProps {
  readonly canManageApplication: false;
  readonly onOpenApplicationActionConfirm?: never;
}

type TeamPublicDetailsModalApplicationRowProps =
  | TeamPublicDetailsModalApplicationRowManageableProps
  | TeamPublicDetailsModalApplicationRowReadOnlyProps;

export const TeamPublicDetailsModalApplicationRow: React.FC<
  TeamPublicDetailsModalApplicationRowProps
> = (props) => {
  const { t } = useTranslation();
  const firstName = props.application.worker.firstName?.trim() ?? "";
  const lastName = props.application.worker.lastName?.trim() ?? "";
  const fullName = `${firstName} ${lastName}`.trim() || "-";
  const motivationalLetter = props.application.motivationalLetter?.trim() ?? "";
  const requestedRateLabel =
    props.application.requestedRate === null ||
    props.application.requestedRate === undefined
      ? "-"
      : `${props.application.requestedRate} ${t("General.Currency")}`;
  const statusChip = toApplicationStatusChip(props.application);
  const applicationAction = (
    type: TeamPublicApplicationAction["type"],
  ): void => {
    if (!props.canManageApplication) return;
    const actionDetails = {
      applicationId: props.application.id,
      applicantName: fullName,
    };
    switch (type) {
      case "assign":
        props.onOpenApplicationActionConfirm({
          type: "assign",
          ...actionDetails,
        });
        return;
      case "decline":
        props.onOpenApplicationActionConfirm({
          type: "decline",
          ...actionDetails,
        });
        return;
      case "withdrawOffer":
        props.onOpenApplicationActionConfirm({
          type: "withdrawOffer",
          ...actionDetails,
        });
        return;
    }
  };

  return (
    <ListRow variant="light">
      <AvatarChip title={fullName} avatarUrl={props.avatarUrl} />
      {props.isPublicShift && (
        <RequestedRateBlock>
          <RequestedRateValue>{requestedRateLabel}</RequestedRateValue>
          <RequestedRateLabel>
            {t("Schedule.TeamPublicRequestedRate")}
          </RequestedRateLabel>
        </RequestedRateBlock>
      )}
      <ActionButtons>
        <SecondaryButton
          size="small"
          label={t("Schedule.TeamPublicMotivationalLetter")}
          disabled={!motivationalLetter}
          onClick={() =>
            props.onOpenMotivationalLetterModal(motivationalLetter)
          }
        />
        {props.canManageApplication &&
          props.application.status === ApplicationStatus.PENDING && (
            <>
              <PrimaryButton
                size="small"
                label={t("Schedule.TeamPublicAssign")}
                styleOverrides={ASSIGN_BUTTON_STYLES}
                disabled={props.isBusy}
                onClick={() => applicationAction("assign")}
              />
              <SecondaryButton
                size="small"
                label={t("Schedule.TeamPublicDecline")}
                styleOverrides={DECLINE_BUTTON_STYLES}
                disabled={props.isBusy}
                onClick={() => applicationAction("decline")}
              />
            </>
          )}
        {props.canManageApplication &&
          props.application.status === ApplicationStatus.OFFERED && (
            <SecondaryButton
              size="small"
              label={t("GigsCompany.CancelOffer")}
              styleOverrides={DECLINE_BUTTON_STYLES}
              disabled={props.isBusy}
              onClick={() => applicationAction("withdrawOffer")}
            />
          )}
      </ActionButtons>
      {statusChip && (
        <ApplicationStatusTagWrapper>
          <Tag
            backgroundColor={statusChip.backgroundColor}
            text={t(statusChip.labelKey)}
            textColor={statusChip.textColor}
          />
        </ApplicationStatusTagWrapper>
      )}
    </ListRow>
  );
};
